import * as React from 'react';
import { useUtilsService } from '../shared/utils/utils';
import {
  BaSeButton,
  BaSeDialogContext,
  BaSeGrid,
  BaSeGridItem,
  BaSeIcon,
  BaSeInput,
  BaSeOptionPicker,
  BaSeSelectableContainer,
  BaSeSmall1,
  BaSeTheme,
} from '@base/react';
import {
  ContainerShare,
  WrapperActionsShare,
  WrapperInputShare,
  WrapperSelectable,
  WrapperSelectContainer,
} from './new-relatorios-styled';

const typeShare = [
  { id: 'ARQUIVO', label: 'Arquivo PDF' },
  { id: 'LINK', label: 'Link dinâmico' },
];

const moreInfoLabels = {
  pdf: 'Será enviado em arquivo estático da situação atual do relatório.',
  link: 'Será enviado um link que permite visualizar os dados atualizados do relatório compartilhado. ',
};

const inputLabels = {
  email: {
    id: 'EMAIL',
    name: 'E-mail',
    label: 'Informe o e-mail',
    placeholder: 'Digite o e-mail',
    nameIcon: 'at',
    message: 'Adicione um e-mail válido. \nEx: seu_email@sebrae.com.br',
  },
  whatsApp: {
    id: 'WHATSAPP',
    name: 'Whatsapp',
    label: 'Informe o número do whatsapp',
    placeholder: 'Digite o número',
    nameIcon: 'whatsapp',
    message:
      'Adicione um número de telefone válido. \nEx: (99)98765-4321 ou (99)3003-1234',
  },
};

const validateLink = [7, 15, 30, 60, 120].map((item, i) => ({
  id: i + 1,
  label: `${item} dias`,
  validate: item,
}));

export const ModalShare = ({
  closeModal,
  filters,
  callback,
  columnsSelected = null,
  tipoRelatorio,
  isMensal = null,
  // eslint-disable-next-line
}) => {
  const { email, whatsApp } = inputLabels;
  const [descricaoCanal, setDescricaoCanal] = React.useState('');
  const [canal, setCanal] = React.useState(email.id);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isInvalidInput, setIsInvalidInput] = React.useState(false);
  const [messageNotification, setMessageNotification] = React.useState('');
  const [typeShareSelected, setTypeShareSelected] = React.useState([
    typeShare[0],
  ]);
  const [validLinkActiveSelected, setvalidLinkActiveSelectedSelected] =
    React.useState([validateLink[0]]);
  const { showNotification } = React.useContext(BaSeDialogContext);
  const { isNotEmpty, validateEmail, parseValue } = useUtilsService();
  const { formatPhoneNumber } = useUtilsService();
  const { azulSebrae36, cinzaSebrae45 } = BaSeTheme.colors.institucionais;
  const descricaoCanalRef = React.useRef(null);

  const isTypeLink = React.useMemo(
    () => typeShareSelected[0].id === 'LINK',
    [typeShareSelected],
  );

  const isEmail = React.useMemo(() => canal === email.id, [canal, email.id]);

  const messageInvalid = React.useCallback((message) => {
    setIsInvalidInput(true);
    setMessageNotification(message);
    descricaoCanalRef.current.focus();
  }, []);

  const validateForm = React.useCallback(() => {
    const isCelular = /^\d{3}\d[9876]/;
    const checkPhoneNumber = /^\d{3}\d[9]/;
    const canalValue = '55' + descricaoCanal.replace(/\D/g, '');
    const numberPhoneSize = canalValue.length;

    if (
      !isEmail &&
      (numberPhoneSize < 12 ||
        (numberPhoneSize === 12 && isCelular.test(canalValue)) ||
        (numberPhoneSize > 12 && !checkPhoneNumber.test(canalValue)))
    ) {
      messageInvalid(whatsApp.message);
      return false;
    }
    if (isEmail && !validateEmail(descricaoCanal)) {
      messageInvalid(email.message);
      return false;
    }
    return true;
  }, [descricaoCanal, email, isEmail, messageInvalid, validateEmail, whatsApp]);

  // eslint-disable-next-line
  const handleCompartilhar = React.useCallback(() => {
    if (!isNotEmpty(descricaoCanal)) {
      messageInvalid(isEmail ? email.message : whatsApp.message);
      return;
    }
    if (!validateForm()) return;
    setIsLoading(true);
    const typeShareId = typeShareSelected[0].id;
    const parsedFilterObj = filters?.reduce((acc, item) => {
      const [key, value] = item.split('=');
      const parsedValue = parseValue(value);

      if (
        key === 'dataInicio' ||
        key === 'dataFim' ||
        key === 'ano' ||
        key === 'mes'
      ) {
        acc[key] = value;
      } else {
        acc[key] = acc[key] ? [...acc[key], parsedValue] : [parsedValue];
      }

      return acc;
    }, {});

    let payload = {
      tipoCompartilhamento: typeShareSelected[0].id,
      tipoCanal: canal,
      descricaoCanal: isEmail
        ? descricaoCanal
        : '55' + descricaoCanal.replace(/\D/g, ''),
      validade:
        typeShareId === 'LINK' ? validLinkActiveSelected[0].validate : null,
      tipoRelatorio: tipoRelatorio,
      filtros: parsedFilterObj,
    };

    if (columnsSelected) payload.filtros.campoCustomizado = columnsSelected;

    if (isMensal !== null) payload.filtros.mensal = isMensal;

    callback(payload)
      .then(() => {
        showNotification({
          message: `Relatório compartilhado com sucesso.`,
          color: 'confirmation',
        });
      })
      .catch((error) => {
        showNotification({
          message: `Não foi possível compartilhar o relatório`,
          supportMessage: error?.message ?? '',
          color: 'destructive',
        });
      })
      .finally(() => {
        setIsLoading(false);
        closeModal();
      });
  }, [
    callback,
    canal,
    closeModal,
    columnsSelected,
    descricaoCanal,
    email,
    filters,
    isEmail,
    isMensal,
    isNotEmpty,
    messageInvalid,
    parseValue,
    showNotification,
    tipoRelatorio,
    typeShareSelected,
    validLinkActiveSelected,
    validateForm,
    whatsApp,
  ]);

  return (
    <ContainerShare>
      <BaSeSmall1 color={cinzaSebrae45}>
        Selecione como deseja compartilhar e informe o contato
      </BaSeSmall1>
      <div>
        <BaSeGrid>
          <BaSeGridItem size={isTypeLink ? 6 : 12}>
            <BaSeOptionPicker
              label="Compartilhar"
              withFilterText={false}
              color="default"
              maxOptionsSelected={1}
              size="medium"
              onChange={setTypeShareSelected}
              values={typeShare}
              initialSelectedValuesId={typeShareSelected?.map(
                (item) => item?.id,
              )}
            />
          </BaSeGridItem>
          {isTypeLink && (
            <BaSeGridItem size={6}>
              <BaSeOptionPicker
                label="Manter link ativo por"
                withFilterText={false}
                color="default"
                maxOptionsSelected={1}
                size="medium"
                onChange={setvalidLinkActiveSelectedSelected}
                values={validateLink}
                initialSelectedValuesId={validLinkActiveSelected?.map(
                  (item) => item?.id,
                )}
              />
            </BaSeGridItem>
          )}
        </BaSeGrid>
      </div>

      <BaSeSmall1 isItalic color={cinzaSebrae45}>
        {isTypeLink ? moreInfoLabels.link : moreInfoLabels.pdf}
      </BaSeSmall1>
      <WrapperSelectContainer>
        {[email, whatsApp].map((item, i) => (
          <BaSeSelectableContainer
            key={i}
            inputProps={{
              checked: canal === item.id,
              disabled: undefined,
              shape: 'circle',
              onChange: () => {
                setCanal((curr) => {
                  if (curr !== item.id) {
                    setDescricaoCanal('');
                    setMessageNotification('');
                    setIsInvalidInput(false);
                  }
                  return item.id;
                });
              },
            }}
            renderContent={() => (
              <WrapperSelectable>
                <BaSeIcon
                  color={azulSebrae36}
                  description="Normal"
                  name={item.nameIcon}
                  size={44}
                />
                <BaSeSmall1 isBold color={azulSebrae36}>
                  {item.name}
                </BaSeSmall1>
              </WrapperSelectable>
            )}
          />
        ))}
      </WrapperSelectContainer>
      <WrapperInputShare>
        <BaSeInput
          ref={descricaoCanalRef}
          value={isEmail ? descricaoCanal : formatPhoneNumber(descricaoCanal)}
          onChange={(e) => {
            if (isNotEmpty(e)) setIsInvalidInput(false);
            setDescricaoCanal(e);
          }}
          onSpecificKeyDown={[
            {
              listenerKey: 'Enter',
              callback: handleCompartilhar,
            },
          ]}
          label={isEmail ? email.label : whatsApp.label}
          placeholder={isEmail ? email.placeholder : whatsApp.placeholder}
          color={isInvalidInput ? 'destructive' : 'default'}
          inputMode={isEmail ? 'email' : 'tel'}
          typeInput={isEmail ? 'email' : 'tel'}
          prefix={isEmail ? '' : '+55 '}
          maxLength={isEmail ? 100 : 19}
          complement={isInvalidInput && messageNotification}
          inputStatus={isInvalidInput ? 'invalid' : 'normal'}
        />
      </WrapperInputShare>
      <WrapperActionsShare>
        <BaSeButton
          buttonType="button"
          color="default"
          isLoading={isLoading}
          onClick={handleCompartilhar}
          size="medium"
          type="primary"
          value="Compartilhar"
        />
        <BaSeButton
          buttonType="button"
          color="default"
          onClick={closeModal}
          size="medium"
          type="secondary"
          value="Cancelar"
        />
      </WrapperActionsShare>
    </ContainerShare>
  );
};
